import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  ControlContainer,
  FormGroup,
  FormGroupDirective,
  Validators,
} from "@angular/forms";
import { Observable, Subject } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";
import { kozteruletTipusok } from "../../constants/kozterulet-tipusok";
import { CimtipusokService } from "../../services/cimtipusok.service";
import { TelepulesekService } from "../../services/telepulesek.service";
import { findByProp } from "../../utils";
import { NgSelectComponent } from "@ng-select/ng-select";
import { OrszagokService } from "../../services/orszagok.service";

@Component({
  selector: "app-torzsek-cim-form",
  templateUrl: "./torzsek-cim-form.component.html",
  styleUrls: ["./torzsek-cim-form.component.scss"],
  viewProviders: [
    { provide: ControlContainer, useExisting: FormGroupDirective },
  ],
})
export class TorzsekCimFormComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport, { static: false })
  cdkVirtualScrollViewPort: CdkVirtualScrollViewport;
  @ViewChild("telepulesSelect", { static: false })
  private telepulesSelect: NgSelectComponent;

  @Input() customStyle: { [key: string]: any } = {};
  @Input() layout: "horizontal" | "vertical" = "vertical";

  formGroup = new FormGroup({});
  formGroupName: "cim" | "cimek" = "cim";
  // Ng select virtual scroll
  telepulesekInit: any[] = [];
  telepulesekSrc: any[] = [];
  telepulesekData: any[] = [];
  orszagokData: any[] = [];
  opened: boolean = false;
  telepules: any;

  cimtipusok$: Observable<any[]>;
  reorderedKozteruletTipusok: any[] = kozteruletTipusok;

  // A checkbox bugos, ha nem unique id-ja van....
  readonly uniqueId: string = new Date().getTime() + "kulfoldi-checkbox-id";
  readonly kozteruletTipusok = kozteruletTipusok;

  private readonly destroy$: Subject<boolean> = new Subject();

  constructor(
    cimtipusokService: CimtipusokService,
    private parent: FormGroupDirective,
    private telepulesekService: TelepulesekService,
    private orszagokService: OrszagokService
  ) {
    this.cimtipusok$ = cimtipusokService.data$;
  }

  ngOnInit(): void {
    this.formGroup = this.parent.form.get("cim") as FormGroup;

    if (!this.formGroup.get("orszag")?.value) {
      this.formGroup.patchValue({
        orszag: "Magyarország",
      });
    }

    this.formGroup
      .get("iranyitoszam")
      .valueChanges.pipe(
        debounceTime(300),
        filter(() => !this.formGroup.get("kulfoldi")?.value),
        takeUntil(this.destroy$)
      )
      .subscribe((irsz: string) => {
        const telepulesek = this.telepulesekSrc.filter(
          (t: any) => t.iranyitoszam === +irsz
        );

        const telepules = findByProp(telepulesek, "nev", this.telepules?.nev);

        if (telepules) {
          this.telepules == telepules;
          this.formGroup.patchValue({
            telepules: telepules?.nev || null,
          });
        } else {
          if (telepulesek.length > 1) {
            this.telepulesekSrc = telepulesek;
            this.telepulesSelect.open();
          } else {
            this.telepulesekSrc = this.telepulesekInit;
            this.telepules = telepules;
            this.formGroup.patchValue({
              telepules:
                this.telepulesekSrc.find((t: any) => t.iranyitoszam === +irsz)
                  ?.nev || null,
            });
          }
        }
      });

    this.telepulesekService.data$
      .pipe(takeUntil(this.destroy$))
      .subscribe((telepulesek: any[]) => {
        this.telepulesekInit = telepulesek;
        this.telepulesekSrc = telepulesek || [];
        this.telepulesekData = (telepulesek || [])
          .slice()
          .filter(
            (value, index, self) =>
              index ===
              self.findIndex((t: { nev: string }) => t.nev === value.nev)
          );
      });

    this.orszagokService.data$
      .pipe(takeUntil(this.destroy$))
      .subscribe((orszagok: any[]) => {
        this.orszagokData = orszagok;
      });

    this.kulfoldiCimChange();
  }

  kulfoldiCimChange() {
    const kulfoldiControl = this.formGroup.get("kulfoldi");
    const kozteruletNeveControl = this.formGroup.get("kozterulet_neve");
    const kozteruletTipusaControl = this.formGroup.get("kozterulet_tipusa");
    const hazszamControl = this.formGroup.get("hazszam");

    if (kulfoldiControl) {
      if (kulfoldiControl.value == true) {
        // Remove validators
        kozteruletNeveControl?.clearValidators();
        kozteruletTipusaControl?.clearValidators();
        hazszamControl?.clearValidators();
      }
      kulfoldiControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((kulfoldi: boolean) => {
          if (kulfoldi) {
            // Remove validators
            kozteruletNeveControl?.clearValidators();
            kozteruletTipusaControl?.clearValidators();
            hazszamControl?.clearValidators();
          } else {
            // Add Validators.required
            //kozteruletNeveControl?.setValidators(Validators.required);
            kozteruletTipusaControl?.setValidators(Validators.required);
            hazszamControl?.setValidators(Validators.required);
          }

          hazszamControl?.updateValueAndValidity();
          kozteruletNeveControl?.updateValueAndValidity();
          kozteruletTipusaControl?.updateValueAndValidity();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  fillIrszOnTelepulesChange(telepules: any): void {
    this.telepules = telepules;
    this.telepulesekSrc = this.telepulesekInit;

    this.formGroup.get("iranyitoszam").patchValue(telepules.iranyitoszam);
  }

  onSearch(searchTerm: any) {
    if (!searchTerm.term) {
      // If the search term is empty, reset the order
      this.reorderedKozteruletTipusok = [...this.kozteruletTipusok];
      return;
    }

    const term = searchTerm.term.toLowerCase();

    let splitTerm = term.split(" ").filter((t) => t);

    this.reorderedKozteruletTipusok = this.kozteruletTipusok.filter((o) => {
      return splitTerm.every((term) =>
        o.toString().toLowerCase().startsWith(term.toLowerCase())
      );
    });

    this.reorderedKozteruletTipusok = this.reorderedKozteruletTipusok.sort(
      (a, b) => {
        const aStr = a?.toString().toLowerCase();
        const bStr = b?.toString().toLowerCase();
        const filterStr = splitTerm[0].toLowerCase();
        const filterLength = filterStr.length;

        const aStartsWithFilter = aStr.startsWith(filterStr);
        const bStartsWithFilter = bStr.startsWith(filterStr);

        const aMatchesLength = aStr.length === filterLength;
        const bMatchesLength = bStr.length === filterLength;

        // Prioritize items that start with the filter string
        if (aStartsWithFilter && !bStartsWithFilter) return -1;
        if (!aStartsWithFilter && bStartsWithFilter) return 1;

        // Within the ones that start with the filter string, prioritize exact-length matches
        if (aStartsWithFilter && bStartsWithFilter) {
          if (aMatchesLength && !bMatchesLength) return -1;
          if (!aMatchesLength && bMatchesLength) return 1;
        }

        // Default to alphabetical sorting
        return aStr.localeCompare(bStr);
      }
    );

    // this.reorderedKozteruletTipusok = this.reorderedKozteruletTipusok.sort((a, b) => {
    //   const aStartsWithFilter = a?.toString().toLowerCase().startsWith(splitTerm[0].toLowerCase());
    //   const bStartsWithFilter = b?.toString().toLowerCase().startsWith(splitTerm[0].toLowerCase());

    //   if (aStartsWithFilter && !bStartsWithFilter) {
    //     return -1;
    //   } else if (!aStartsWithFilter && bStartsWithFilter) {
    //     return 1;
    //   } else {
    //     return 0;
    //   }
    // });
  }

  startsWithFirstWord(cikknev: string, term: string): boolean {
    const firstWord = cikknev.split(" ")[0].toLowerCase(); // Extract the first word
    return firstWord.startsWith(term); // Check if the first word starts with the search term
  }

  onSelectFocus(): void {
    this.reorderedKozteruletTipusok = kozteruletTipusok;
  }
}
